import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GitPullRequest } from 'lucide-react';
import { useState } from 'react';
import { diffContent, type DocumentContent, type DocumentDetailDto, type UserSummary } from '@imsenta/shared';
import { Alert, Button, Checkbox, Field, Input, Modal, Textarea, useToast } from '@/components/ui';
import { DiffSummary } from '@/components/DiffView';
import { api, ApiError } from '@/lib/api';
import { useSession } from '@/lib/session';

/**
 * Turns what the author has in the editor into a change request.
 *
 * The content is sent with the proposal rather than the server re-reading the
 * published version, so the edits the author just made are what reviewers see.
 */
export function ProposeChangeModal({
  open,
  onClose,
  documentId,
  documentTitle,
  content,
  title,
  metadata,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  documentId: string;
  documentTitle: string;
  content: DocumentContent;
  title: string;
  metadata: Record<string, unknown>;
  onCreated: (changeRequestId: string) => void;
}) {
  const queryClient = useQueryClient();
  const { push } = useToast();
  const { session } = useSession();
  const [summary, setSummary] = useState('');
  const [description, setDescription] = useState('');
  const [reviewerIds, setReviewerIds] = useState<string[]>([]);
  const [submit, setSubmit] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { data: doc } = useQuery({
    queryKey: ['document', documentId],
    queryFn: () => api.get<DocumentDetailDto>(`/documents/${documentId}`),
    enabled: open,
  });

  const { data: members } = useQuery({
    queryKey: ['members'],
    queryFn: () => api.get<{ items: (UserSummary & { role: string })[] }>('/tenant/members'),
    enabled: open,
    retry: false,
  });

  const baseContent = doc?.published?.content;
  const diff = baseContent ? diffContent(baseContent, content) : null;
  const hasChanges = diff
    ? diff.summary.sectionsAdded + diff.summary.sectionsRemoved + diff.summary.sectionsModified + diff.summary.sectionsRenamed > 0
    : true;

  const create = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>('/change-requests', {
        documentId,
        title: summary.trim(),
        description: description || undefined,
        // The author's working content is the proposal.
        content,
        proposedTitle: title,
        metadata,
        reviewerIds,
        submit,
      }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['change-requests'] });
      queryClient.invalidateQueries({ queryKey: ['document', documentId] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      push(submit ? 'Change request opened for review' : 'Change request saved as a draft', 'success');
      onClose();
      onCreated(created.id);
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not create the change request'),
  });

  const eligibleReviewers = (members?.items ?? []).filter((m) => m.id !== session?.user.id && m.role !== 'reader');

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Propose this change"
      description={`Your edits to ${documentTitle} become a proposal for review. The published version is untouched until it is approved.`}
      width="lg"
      footer={
        <>
          <Button onClick={onClose}>Keep editing</Button>
          <Button
            variant="primary"
            icon={<GitPullRequest className="size-4" />}
            loading={create.isPending}
            disabled={!summary.trim() || !hasChanges}
            onClick={() => {
              setError(null);
              create.mutate();
            }}
          >
            {submit ? 'Open for review' : 'Save as draft'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error ? <Alert tone="danger">{error}</Alert> : null}

        {diff ? (
          hasChanges ? (
            <div className="rounded-lg border border-ink-200 bg-ink-50 px-3.5 py-3">
              <p className="mb-1.5 text-[11.5px] font-semibold tracking-wide text-ink-500 uppercase">What you are proposing</p>
              <DiffSummary diff={diff} />
            </div>
          ) : (
            <Alert tone="warning" title="Nothing to propose yet">
              Your version matches the published one. Make an edit first, then come back.
            </Alert>
          )
        ) : null}

        <Field label="Title" required hint="A short summary of the change. Reviewers see this first.">
          <Input
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="Add post-incident review trigger"
            autoFocus
          />
        </Field>

        <Field label="Rationale" hint="Why is this needed? What prompted it?">
          <Textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>

        {eligibleReviewers.length ? (
          <Field label="Request review from" hint="Optional. They will see it under “Awaiting my review”.">
            <div className="max-h-40 space-y-1.5 overflow-y-auto rounded-lg border border-ink-200 p-2.5">
              {eligibleReviewers.map((member) => (
                <Checkbox
                  key={member.id}
                  label={
                    <span className="inline-flex items-center gap-1.5">
                      {member.name}
                      <span className="text-ink-400 capitalize">· {member.role}</span>
                    </span>
                  }
                  checked={reviewerIds.includes(member.id)}
                  onChange={(e) =>
                    setReviewerIds((current) => (e.target.checked ? [...current, member.id] : current.filter((id) => id !== member.id)))
                  }
                />
              ))}
            </div>
          </Field>
        ) : null}

        <Checkbox
          label="Open for review now"
          hint="Leave unticked to keep refining it privately first."
          checked={submit}
          onChange={(e) => setSubmit(e.target.checked)}
        />
      </div>
    </Modal>
  );
}
